import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Package, Clock, CheckCircle2, Truck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EstimateWidget from '../components/EstimateWidget';
import USMap from '../components/USMap';
import { Button } from '../components/ui/button';
import SEO from '../seo/SEO';
import { localBusinessSchema } from '../seo/schemas';
import { SITE_URL } from '../seo/siteConfig';
import {
  STATE_ZONES,
  ZONE_LEGEND,
  getZoneInfo,
  ORIGIN,
  CODE_TO_STATE_NAME,
} from '../data/zones';
import {
  STATE_SLUG_TO_CODE,
  ALL_STATE_SLUGS,
  getStateMeta,
  slugifyState,
} from '../data/states';

// Service schema specific to this state — boosts service-area + locale signals
function stateServiceSchema(stateName, code) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Third-party logistics (3PL) fulfillment',
    name: `Ecommerce Fulfillment for ${stateName} Brands`,
    provider: {
      '@type': 'Organization',
      name: 'Shipmaster Fulfillment',
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'State',
      name: stateName,
    },
    description: `Modern 3PL fulfillment for Shopify and DTC brands shipping to or from ${stateName}. Same-day shipping, transparent pricing, no minimums.`,
    url: `${SITE_URL}/zones/${slugifyState(stateName)}`,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: '2',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: 2,
        priceCurrency: 'USD',
        unitText: 'order',
      },
    },
  };
}

// Geographic neighbors for cross-linking — one of the most under-rated SEO
// patterns. Each state's page links to 4-6 nearby states, building a dense
// internal-link graph that Google rewards heavily.
const NEIGHBORS = {
  AL: ['MS', 'TN', 'GA', 'FL'],
  AK: ['WA', 'OR', 'CA', 'HI'],
  AZ: ['CA', 'NV', 'UT', 'NM', 'CO'],
  AR: ['MO', 'OK', 'TX', 'LA', 'MS', 'TN'],
  CA: ['OR', 'NV', 'AZ'],
  CO: ['WY', 'NE', 'KS', 'OK', 'NM', 'AZ', 'UT'],
  CT: ['NY', 'MA', 'RI'],
  DE: ['MD', 'PA', 'NJ'],
  DC: ['MD', 'VA'],
  FL: ['GA', 'AL'],
  GA: ['FL', 'AL', 'TN', 'NC', 'SC'],
  HI: ['CA', 'AK'],
  ID: ['WA', 'OR', 'NV', 'UT', 'WY', 'MT'],
  IL: ['WI', 'IA', 'MO', 'KY', 'IN'],
  IN: ['MI', 'OH', 'KY', 'IL'],
  IA: ['MN', 'WI', 'IL', 'MO', 'NE', 'SD'],
  KS: ['NE', 'MO', 'OK', 'CO'],
  KY: ['IN', 'OH', 'WV', 'VA', 'TN', 'MO', 'IL'],
  LA: ['TX', 'AR', 'MS'],
  ME: ['NH', 'MA', 'VT'],
  MD: ['DE', 'PA', 'WV', 'VA', 'DC'],
  MA: ['NH', 'VT', 'CT', 'RI', 'NY'],
  MI: ['WI', 'IN', 'OH'],
  MN: ['ND', 'SD', 'IA', 'WI'],
  MS: ['LA', 'AR', 'TN', 'AL'],
  MO: ['IA', 'IL', 'KY', 'TN', 'AR', 'OK', 'KS', 'NE'],
  MT: ['ID', 'WY', 'SD', 'ND'],
  NE: ['SD', 'IA', 'MO', 'KS', 'CO', 'WY'],
  NV: ['CA', 'OR', 'ID', 'UT', 'AZ'],
  NH: ['VT', 'ME', 'MA'],
  NJ: ['NY', 'PA', 'DE'],
  NM: ['AZ', 'UT', 'CO', 'OK', 'TX'],
  NY: ['VT', 'MA', 'CT', 'NJ', 'PA'],
  NC: ['VA', 'TN', 'GA', 'SC'],
  ND: ['MN', 'SD', 'MT'],
  OH: ['MI', 'PA', 'WV', 'KY', 'IN'],
  OK: ['KS', 'MO', 'AR', 'TX', 'NM', 'CO'],
  OR: ['WA', 'ID', 'NV', 'CA'],
  PA: ['NY', 'NJ', 'DE', 'MD', 'WV', 'OH'],
  RI: ['MA', 'CT'],
  SC: ['NC', 'GA'],
  SD: ['ND', 'MN', 'IA', 'NE', 'WY', 'MT'],
  TN: ['KY', 'VA', 'NC', 'GA', 'AL', 'MS', 'AR', 'MO'],
  TX: ['NM', 'OK', 'AR', 'LA'],
  UT: ['ID', 'WY', 'CO', 'NM', 'AZ', 'NV'],
  VT: ['NH', 'MA', 'NY'],
  VA: ['MD', 'WV', 'KY', 'TN', 'NC', 'DC'],
  WA: ['OR', 'ID'],
  WV: ['PA', 'MD', 'VA', 'KY', 'OH'],
  WI: ['MI', 'MN', 'IA', 'IL'],
  WY: ['MT', 'SD', 'NE', 'CO', 'UT', 'ID'],
};

export default function StatePage() {
  const { slug } = useParams();
  const meta = getStateMeta(slug);
  if (!meta) {
    // Unknown slug — redirect to /zones rather than 404 for cleaner UX
    return <Navigate to="/zones" replace />;
  }

  const zone = STATE_ZONES[meta.code];
  const zoneInfo = getZoneInfo(zone);
  const neighbors = (NEIGHBORS[meta.code] || []).map((code) => ({
    code,
    name: CODE_TO_STATE_NAME[code],
    slug: slugifyState(CODE_TO_STATE_NAME[code]),
    zone: STATE_ZONES[code],
    label: getZoneInfo(STATE_ZONES[code])?.label,
  }));

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title={`Ecommerce Fulfillment for ${meta.name} Brands — ${zoneInfo.label} from FL`}
        description={`Shopify and DTC fulfillment for ${meta.name}. ${zoneInfo.label} transit from our Weston, FL warehouse. $2 per order, no minimums, no contracts.`}
        keywords={`${meta.name} 3PL, ${meta.name} fulfillment, ecommerce fulfillment ${meta.name}, Shopify 3PL ${meta.name}, ${meta.name} shipping, fulfillment center for ${meta.name}`}
        schemas={[localBusinessSchema(), stateServiceSchema(meta.name, meta.code)]}
      />
      <Header />
      <main>
        {/* Hero */}
        <section
          className="relative overflow-hidden text-white"
          style={{
            background:
              'linear-gradient(135deg, #1e1b4b 0%, #312e81 35%, #4f46e5 100%)',
          }}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <Link
              to="/zones"
              className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-200 hover:text-white transition-colors"
              data-testid="state-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back to zone map
            </Link>
            <div className="mt-6 flex items-center gap-3">
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-xs font-bold uppercase tracking-widest"
                style={{ color: '#ffffff' }}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: zoneInfo.color }}
                />
                Zone {zone} — {zoneInfo.label}
              </span>
            </div>
            <h1 className="mt-4 text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              Ecommerce Fulfillment for {meta.name} Brands
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-indigo-100 leading-relaxed">
              {meta.blurb}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                size="lg"
                className="bg-white text-indigo-700 hover:bg-indigo-50 rounded-full font-bold h-12 px-7 shadow-lg"
                data-testid="state-hero-cta"
              >
                <Link to="/#contact">Get a Free Estimate</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-white/40 text-white hover:bg-white/10 rounded-full font-bold h-12 px-7"
              >
                <Link to="/integrations">View Integrations</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Stats strip */}
        <section className="border-b border-slate-100 bg-slate-50/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Stat
              icon={<Clock className="w-5 h-5" />}
              label="Transit time"
              value={zoneInfo.label}
            />
            <Stat
              icon={<MapPin className="w-5 h-5" />}
              label="Origin"
              value={ORIGIN.city}
            />
            <Stat
              icon={<Truck className="w-5 h-5" />}
              label="Carriers"
              value="UPS · USPS · FedEx"
            />
            <Stat
              icon={<Package className="w-5 h-5" />}
              label="Per-order rate"
              value="$2 · No minimums"
            />
          </div>
        </section>

        {/* Use cases */}
        <section className="py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-1">
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
                Who we serve in {meta.name}
              </p>
              <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
                Common use cases
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Brands that ship to or from {meta.name} use Shipmaster across a
                handful of repeatable patterns. Pick the one closest to yours
                and book a call.
              </p>
            </div>
            <ul className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
              {meta.useCases.map((u) => (
                <li
                  key={u}
                  className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-slate-200 hover:border-indigo-200 hover:shadow-sm transition-all"
                >
                  <CheckCircle2
                    className="flex-shrink-0 w-5 h-5 text-indigo-600 mt-0.5"
                    strokeWidth={2.5}
                  />
                  <span className="text-sm font-medium text-slate-800 leading-snug">
                    {u}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Map context — visual reinforcement of the state's zone */}
        <section className="py-14 sm:py-20 bg-slate-50/60 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
              Where {meta.name} sits on our map
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
              {meta.name} is in Zone {zone}
            </h2>
            <p className="mt-3 max-w-2xl text-slate-600 leading-relaxed">
              From our Weston, FL warehouse, ground delivery to {meta.name} runs{' '}
              {zoneInfo.label.toLowerCase()}. Hover any other state below to
              compare transit times.
            </p>
            <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-4 sm:p-6 shadow-sm">
              <USMap highlightCode={meta.code} interactive={false} />
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                {ZONE_LEGEND.map((z) => (
                  <div key={z.zone} className="flex items-center gap-1.5 text-xs">
                    <span
                      className="w-3 h-3 rounded-sm"
                      style={{ backgroundColor: z.color }}
                    />
                    <span className="font-semibold" style={{ color: z.textColor }}>
                      Z{z.zone}
                    </span>
                    <span className="text-slate-500">{z.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA — embedded estimate widget so visitors can convert without
             leaving the page. This is the page's primary conversion path. */}
        <section className="py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
                Free instant estimate
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
                See what shipping from FL to {meta.name} could save you
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Drop your store URL and monthly order volume — we&apos;ll show
                you a real-time savings estimate based on your actual lane.
              </p>
            </div>
            <div className="mt-10 max-w-3xl mx-auto">
              <EstimateWidget />
            </div>
          </div>
        </section>

        {/* Cross-link to neighboring states — the SEO multiplier */}
        {neighbors.length > 0 && (
          <section className="py-14 bg-slate-50/60 border-t border-slate-100">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
                Nearby states
              </p>
              <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
                We also fulfill into neighboring states
              </h2>
              <ul className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {neighbors.map((n) => (
                  <li key={n.code}>
                    <Link
                      to={`/zones/${n.slug}`}
                      className="group flex items-center justify-between p-4 rounded-xl bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all"
                      data-testid={`neighbor-link-${n.code.toLowerCase()}`}
                    >
                      <div>
                        <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-700">
                          {n.name}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Zone {n.zone} · {n.label}
                        </div>
                      </div>
                      <span className="text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity text-sm">
                        →
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-7">
                <Link
                  to="/zones"
                  className="inline-flex items-center gap-2 text-sm font-bold text-indigo-600 hover:text-indigo-700"
                  data-testid="state-all-states-link"
                >
                  View all 50 states on the zone map →
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
          {label}
        </div>
        <div className="mt-0.5 text-sm font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

// Re-export for sitemap generators / other consumers
export { ALL_STATE_SLUGS };
